/**
 * The Color — Shared Utilities
 * Common helper functions used across theme JavaScript.
 * Loaded before all other JS files.
 */

(function () {
  'use strict';

  /**
   * Format price in GBP (pence to pounds)
   * @param {number} pence - Price in pence
   * @returns {string} Formatted price string e.g. "£12.50"
   */
  function formatMoney(pence) {
    return '£' + (pence / 100).toFixed(2);
  }

  /**
   * Escape a string for safe rendering as HTML text content
   * @param {string} text - Raw string
   * @returns {string} HTML-escaped string
   */
  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Escape a string for safe use inside HTML attributes
   * @param {string} str - Raw string
   * @returns {string} Attribute-safe string
   */
  function escapeAttr(str) {
    if (!str) return '';
    return str
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  /**
   * Check if user accepted analytics cookies (GDPR).
   * Returns true ONLY when tc_cookie_consent === 'accepted'.
   * @returns {boolean}
   */
  function hasConsent() {
    var match = document.cookie.match(/(?:^|; )tc_cookie_consent=([^;]*)/);
    return match ? decodeURIComponent(match[1]) === 'accepted' : false;
  }

  // Expose on global namespace
  window.TheColor = window.TheColor || {};
  window.TheColor.utils = {
    formatMoney: formatMoney,
    escapeHtml: escapeHtml,
    escapeAttr: escapeAttr,
    hasConsent: hasConsent,
  };
})();

/**
 * TheColor.Scroller — the one horizontal rail (contract C1, 2026-10-02).
 *
 * Markup:  <div class="tc-scroller" data-tc-scroller>
 *            <div class="tc-scroller__track"> …items… </div>
 *          </div>
 * Styles live in base.css §16. For every [data-tc-scroller] this adds, once:
 *  - .is-scrollable / .has-more-left / .has-more-right on the root (edge fade);
 *  - "Scroll left" / "Scroll right" buttons (.tc-scroller__btn), placed on
 *    the track's content edges and centred on the first item's image;
 *  - a progress bar and a "1–2 of 12" line (.tc-scroller__meta, aria-hidden:
 *    the items themselves are what assistive tech reads).
 * Options in the attribute value: "no-buttons", "no-meta", "no-fade".
 * data-tc-scroller-anchor="<selector>" picks what the buttons centre on.
 *
 * Auto-inits after parse, on 'shopify:section:load', and on any scroller
 * inserted later (fetched recommendation rails, drawer re-renders).
 * Idempotent. Physical left/right throughout, so RTL works unchanged.
 * Respects prefers-reduced-motion for button scrolling.
 *
 * API: TheColor.Scroller.init(root?)  — set up scrollers inside root
 *      TheColor.Scroller.refresh(el?) — re-measure one (or every) scroller
 *      TheColor.Scroller.get(el)      — the instance for a root element
 * Events: 'tc:scroller:update' on the root after each change
 *         (detail: { first, last, total, atStart, atEnd }).
 */
(function () {
  'use strict';

  var SELECTOR = '[data-tc-scroller]';
  var DEFAULT_ANCHOR = '.tc-product-card__media, .tc-collection-card__image-wrapper, img, picture';
  var BTN = 44;
  var uid = 0;
  var instances = [];
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var resizeObserver = 'ResizeObserver' in window ? new ResizeObserver(function (entries) {
    for (var i = 0; i < entries.length; i++) {
      var inst = entries[i].target.__tcScroller;
      if (inst) inst.schedule(true);
    }
  }) : null;

  var ICONS = {
    prev: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m15 18-6-6 6-6"/></svg>',
    next: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="m9 18 6-6-6-6"/></svg>'
  };

  function hasOpt(root, name) {
    return (' ' + (root.getAttribute('data-tc-scroller') || '') + ' ').indexOf(' ' + name + ' ') !== -1;
  }

  function isItem(el) {
    if (el.nodeType !== 1) return false;
    var tag = el.tagName;
    if (tag === 'TEMPLATE' || tag === 'SCRIPT' || tag === 'STYLE' || el.hidden) return false;
    return el.getClientRects().length > 0;
  }

  function Scroller(root) {
    this.root = root;
    this.track = root.querySelector('.tc-scroller__track');
    this.raf = 0;
    this.needsMeasure = true;
    this.geom = null;
    this.onScroll = this.schedule.bind(this, false);
    this.setup();
  }

  Scroller.prototype.setup = function () {
    var root = this.root;
    var track = this.track;
    var self = this;
    root.__tcScroller = this;
    track.__tcScroller = this;
    root.setAttribute('data-tc-scroller-ready', '');
    if (!track.id) track.id = 'tc-scroller-track-' + (++uid);

    // Buttons go before the track's top-level wrapper: controls first in the
    // tab order, the items after (WAI carousel pattern). Visually they float.
    var before = track;
    while (before.parentElement && before.parentElement !== root) before = before.parentElement;

    if (!hasOpt(root, 'no-buttons')) {
      this.prev = this.makeButton('prev', 'Scroll left');
      this.next = this.makeButton('next', 'Scroll right');
      root.insertBefore(this.prev, before);
      root.insertBefore(this.next, before);
      this.prev.addEventListener('click', function () { self.go(-1); });
      this.next.addEventListener('click', function () { self.go(1); });
    }

    if (!hasOpt(root, 'no-meta')) {
      var meta = document.createElement('div');
      meta.className = 'tc-scroller__meta';
      meta.setAttribute('aria-hidden', 'true');
      meta.innerHTML = '<span class="tc-scroller__bar"><span class="tc-scroller__thumb"></span></span><span class="tc-scroller__status"></span>';
      root.appendChild(meta);
      this.meta = meta;
      this.bar = meta.firstChild;
      this.thumb = this.bar.firstChild;
      // Update a text node's data (characterData), not textContent, so the
      // page-wide childList observer below isn't woken on every scroll frame.
      this.statusText = document.createTextNode('');
      meta.lastChild.appendChild(this.statusText);
    }

    track.addEventListener('scroll', this.onScroll, { passive: true });
    if (resizeObserver) {
      resizeObserver.observe(track);
      resizeObserver.observe(root);
    } else {
      window.addEventListener('resize', this.schedule.bind(this, true), { passive: true });
    }
    if ('MutationObserver' in window) {
      this.mo = new MutationObserver(function () { self.schedule(true); });
      this.mo.observe(track, { childList: true });
    }
    // Late images change the anchor height; re-centre the buttons.
    track.addEventListener('load', function () { self.schedule(true); }, true);

    this.schedule(true);
  };

  Scroller.prototype.makeButton = function (dir, label) {
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'tc-scroller__btn tc-scroller__btn--' + dir;
    btn.setAttribute('aria-label', label);
    btn.setAttribute('aria-controls', this.track.id);
    btn.innerHTML = ICONS[dir];
    return btn;
  };

  Scroller.prototype.items = function () {
    var out = [];
    var kids = this.track.children;
    for (var i = 0; i < kids.length; i++) {
      if (isItem(kids[i])) out.push(kids[i]);
    }
    return out;
  };

  Scroller.prototype.schedule = function (measure) {
    if (measure) this.needsMeasure = true;
    if (this.raf) return;
    var self = this;
    this.raf = window.requestAnimationFrame(function () {
      self.raf = 0;
      if (!self.root.isConnected) return;
      self.update();
    });
  };

  // Geometry that only changes on resize / content change.
  Scroller.prototype.measure = function () {
    var track = this.track;
    var cs = window.getComputedStyle(track);
    var rootRect = this.root.getBoundingClientRect();
    var trackRect = track.getBoundingClientRect();
    var padL = parseFloat(cs.paddingLeft) || 0;
    var padR = parseFloat(cs.paddingRight) || 0;
    var gap = parseFloat(cs.columnGap) || 0;
    var items = this.items();
    var anchorY = trackRect.top + trackRect.height / 2;
    if (items.length) {
      var sel = this.root.getAttribute('data-tc-scroller-anchor') || DEFAULT_ANCHOR;
      var anchor = null;
      try { anchor = items[0].matches(sel) ? items[0] : items[0].querySelector(sel); } catch (e) { anchor = null; }
      if (anchor) {
        var ar = anchor.getBoundingClientRect();
        if (ar.height > 0) anchorY = ar.top + ar.height / 2;
      }
    }
    // A scroll region with nothing focusable inside still has to be
    // reachable by keyboard (axe: scrollable-region-focusable). Re-checked
    // on every measure, because rails often fill in after init.
    var focusable = !!track.querySelector('a[href], button, input, select, textarea, [tabindex]:not(.tc-scroller__track)');
    if (items.length && !focusable && !track.hasAttribute('tabindex')) {
      track.setAttribute('tabindex', '0');
      this.ownTabindex = true;
    } else if (focusable && this.ownTabindex) {
      track.removeAttribute('tabindex');
      this.ownTabindex = false;
    }
    this.geom = {
      rtl: cs.direction === 'rtl',
      padL: padL,
      padR: padR,
      gap: gap,
      // Content edges of the track, relative to the root.
      contentL: trackRect.left + padL - rootRect.left,
      contentR: trackRect.right - padR - rootRect.left,
      rootW: rootRect.width,
      btnY: anchorY - rootRect.top
    };
    this.needsMeasure = false;
  };

  Scroller.prototype.update = function () {
    if (this.needsMeasure || !this.geom) this.measure();
    var g = this.geom;
    var track = this.track;
    var max = track.scrollWidth - track.clientWidth;
    var scrollable = max > 2;
    var sl = track.scrollLeft;
    // Distance already scrolled from the physical left / still to the right.
    // RTL scrollLeft runs 0 → -max, so normalise to physical terms.
    var fromLeft = g.rtl ? max + sl : sl;
    fromLeft = Math.max(0, Math.min(max, fromLeft));
    var fromRight = max - fromLeft;
    var moreLeft = scrollable && fromLeft > 2;
    var moreRight = scrollable && fromRight > 2;

    // Which items are mostly (≥60%) inside the content window.
    var items = this.items();
    var trackRect = track.getBoundingClientRect();
    var winL = trackRect.left + g.padL - 1;
    var winR = trackRect.right - g.padR + 1;
    var first = -1;
    var last = -1;
    for (var i = 0; i < items.length; i++) {
      var r = items[i].getBoundingClientRect();
      var vis = Math.min(r.right, winR) - Math.max(r.left, winL);
      if (r.width > 0 && vis >= r.width * 0.6) {
        if (first < 0) first = i;
        last = i;
      }
    }
    if (first < 0 && items.length) { first = 0; last = 0; }
    var barW = this.bar ? this.bar.clientWidth : 0;

    // ---- writes ----
    var root = this.root;
    root.classList.toggle('is-scrollable', scrollable);
    root.classList.toggle('has-more-left', moreLeft);
    root.classList.toggle('has-more-right', moreRight);

    if (this.prev) {
      var half = BTN / 2;
      var y = Math.max(half, g.btnY);
      var lx = Math.max(4, Math.min(g.contentL - half, g.rootW - BTN * 2 - 8));
      var rx = Math.min(g.rootW - BTN - 4, Math.max(g.contentR - half, BTN + 8));
      this.prev.style.top = y + 'px';
      this.next.style.top = y + 'px';
      this.prev.style.left = lx + 'px';
      this.next.style.left = rx + 'px';
      this.setDisabled(this.prev, !moreLeft, this.next);
      this.setDisabled(this.next, !moreRight, this.prev);
    }

    if (this.meta) {
      this.meta.style.left = Math.max(0, g.contentL) + 'px';
      this.meta.style.right = Math.max(0, g.rootW - g.contentR) + 'px';
      var ratio = track.scrollWidth > 0 ? Math.min(1, track.clientWidth / track.scrollWidth) : 1;
      var thumbW = Math.max(16, barW * ratio);
      var x = max > 0 ? (barW - thumbW) * (fromLeft / max) : 0;
      this.thumb.style.width = thumbW + 'px';
      this.thumb.style.transform = 'translateX(' + x.toFixed(1) + 'px)';
      var text = '';
      if (items.length && first >= 0) {
        text = (first === last ? String(first + 1) : (first + 1) + '–' + (last + 1)) + ' of ' + items.length;
      }
      if (this.statusText.data !== text) this.statusText.data = text;
    }

    var detail = { first: first, last: last, total: items.length, atStart: !moreLeft, atEnd: !moreRight };
    var key = [scrollable, first, last, items.length, moreLeft, moreRight].join('|');
    if (key !== this.lastKey) {
      this.lastKey = key;
      try { root.dispatchEvent(new CustomEvent('tc:scroller:update', { detail: detail })); } catch (e) { /* old engines */ }
    }
  };

  // A button that runs out of room hides; if it held focus, hand focus to
  // its twin (or the track) so keyboard users aren't dropped on <body>.
  Scroller.prototype.setDisabled = function (btn, off, twin) {
    if (btn.disabled === off) return;
    if (off && document.activeElement === btn) {
      if (twin && !twin.disabled) twin.focus();
      else {
        if (!this.track.hasAttribute('tabindex')) { this.track.setAttribute('tabindex', '-1'); this.ownTabindex = true; }
        this.track.focus({ preventScroll: true });
      }
    }
    btn.disabled = off;
  };

  // dir: -1 = physical left, 1 = physical right. One "page" = the items that
  // fit fully; snapping lands it on an item edge.
  Scroller.prototype.go = function (dir) {
    if (this.needsMeasure || !this.geom) this.measure();
    var track = this.track;
    var items = this.items();
    var view = track.clientWidth - this.geom.padL - this.geom.padR;
    var step = view * 0.9;
    if (items.length) {
      var w = items[0].getBoundingClientRect().width;
      var gap = this.geom.gap;
      if (w > 0) {
        var perView = Math.max(1, Math.floor((view + gap + 1) / (w + gap)));
        step = perView * (w + gap);
      }
    }
    track.scrollBy({ left: dir * step, behavior: reduceMotion.matches ? 'auto' : 'smooth' });
  };

  Scroller.prototype.destroy = function () {
    if (this.raf) window.cancelAnimationFrame(this.raf);
    this.track.removeEventListener('scroll', this.onScroll);
    if (resizeObserver) {
      resizeObserver.unobserve(this.track);
      resizeObserver.unobserve(this.root);
    }
    if (this.mo) this.mo.disconnect();
    if (this.prev) { this.prev.remove(); this.next.remove(); }
    if (this.meta) this.meta.remove();
    if (this.ownTabindex) this.track.removeAttribute('tabindex');
    ['is-scrollable', 'has-more-left', 'has-more-right'].forEach(function (c) { this.root.classList.remove(c); }, this);
    this.root.removeAttribute('data-tc-scroller-ready');
    delete this.root.__tcScroller;
    delete this.track.__tcScroller;
  };

  function prune() {
    instances = instances.filter(function (inst) {
      if (inst.root.isConnected && inst.track.isConnected) return true;
      inst.destroy();
      return false;
    });
  }

  function init(scope) {
    var base = scope && scope.querySelectorAll ? scope : document;
    var list = [];
    if (base.matches && base.matches(SELECTOR)) list.push(base);
    var found = base.querySelectorAll(SELECTOR);
    for (var i = 0; i < found.length; i++) list.push(found[i]);
    prune();
    for (var j = 0; j < list.length; j++) {
      var root = list[j];
      if (root.__tcScroller) continue;
      if (!root.querySelector('.tc-scroller__track')) continue;
      instances.push(new Scroller(root));
    }
  }

  function refresh(el) {
    if (el && el.__tcScroller) { el.__tcScroller.schedule(true); return; }
    instances.forEach(function (inst) { inst.schedule(true); });
  }

  function get(el) {
    return (el && el.__tcScroller) || null;
  }

  window.TheColor = window.TheColor || {};
  window.TheColor.Scroller = { init: init, refresh: refresh, get: get };

  function boot() {
    init(document);
    // Scrollers inserted later (fetched PDP rails, drawer upsells): one cheap
    // check per frame at most.
    if ('MutationObserver' in window && document.body) {
      var pending = false;
      new MutationObserver(function () {
        if (pending) return;
        pending = true;
        window.requestAnimationFrame(function () {
          pending = false;
          if (document.querySelector(SELECTOR + ':not([data-tc-scroller-ready])')) init(document);
          else if (instances.length) prune();
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    instances = instances.filter(function (inst) {
      if (e.target.contains(inst.root)) { inst.destroy(); return false; }
      return true;
    });
  });
  // Fonts and late layout change item widths.
  window.addEventListener('load', function () { refresh(); });
})();

/**
 * TheColor.track(name, params) — one queued door for theme analytics events
 * (sweep B8-21 / B1-27). Safe default until the owner decides GA4 and
 * send_to routing (B12): events wait for analytics consent and for a Google
 * tag `config` in dataLayer (polled every 250 ms, sent anyway after 15 s),
 * then go out through gtag exactly as the callers send them today. Routing
 * is one switch: set TheColor.track.sendTo (e.g. 'AW-…' or ['G-…','AW-…'])
 * and every queued call carries it. No caller uses this yet — they move over
 * in their own batches. Consent withdrawal empties the queue.
 */
(function () {
  'use strict';

  var queue = [];
  var timer = 0;
  var started = 0;
  var POLL = 250;
  var GIVE_UP = 15000;

  function consented() {
    var u = window.TheColor && window.TheColor.utils;
    return !!(u && u.hasConsent && u.hasConsent());
  }

  function tagConfigured() {
    var dl = window.dataLayer;
    if (!dl || !dl.length) return false;
    for (var i = 0; i < dl.length; i++) {
      var e = dl[i];
      if (e && e[0] === 'config') return true;
    }
    return false;
  }

  function send(item) {
    var params = {};
    var src = item[1] || {};
    for (var k in src) {
      if (Object.prototype.hasOwnProperty.call(src, k)) params[k] = src[k];
    }
    var to = track.sendTo;
    if (to && !params.send_to) params.send_to = to;
    window.gtag('event', item[0], params);
  }

  function flush() {
    timer = 0;
    if (!queue.length) return;
    if (!consented()) return; // resumes on tc:consent-granted
    if (typeof window.gtag !== 'function') { wait(); return; }
    if (!tagConfigured() && Date.now() - started < GIVE_UP) { wait(); return; }
    while (queue.length) {
      try { send(queue.shift()); } catch (e) { /* never break the caller */ }
    }
  }

  function wait() {
    if (!timer) timer = window.setTimeout(flush, POLL);
  }

  function track(name, params) {
    if (typeof name !== 'string' || !name) return;
    if (!queue.length) started = Date.now();
    queue.push([name, params || {}]);
    flush();
  }

  track.sendTo = null;
  track.flush = flush;

  document.addEventListener('tc:consent-granted', function () {
    started = Date.now();
    flush();
  });
  document.addEventListener('tc:consent-withdrawn', function () {
    queue.length = 0;
  });

  window.TheColor = window.TheColor || {};
  window.TheColor.track = track;
})();
